import { useEffect } from 'react';
import { Accordion, Anchor, Box, Heading, Icon, Paragraph } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import {
  BackgroundReportAlert,
  getBackgroundReportAlerts,
  getFirstReportAlert,
  isReportUnavailable,
} from '../BackgroundReportEvictions/BackgroundReportAlerts';
import { CriminalSectionContent } from './CriminalSectionContent';
import { getTitleCase } from '../../../utils/formatters';
import { CONDITIONAL_APPROVAL_URL, isSexOffenderRecord, pluralize } from '@zg-rentals/applications-utils';
import type { ApplicationData, ConditionallyApprove, EmitMetric, Log } from '../../../models/report-models';
import { ReportType, UserType } from '../../../models/report-models';
import { CriminalSectionObfuscatedContent } from './CriminalSectionContent/CriminalSectionObfuscatedContent';
import { countReportData } from '../../../utils/report-utils';

export function BackgroundReportCriminal({
  userType,
  backgroundReport,
  applicationData,
  emitMetric,
  log,
  requiresConditionalApproval,
  conditionallyApprove,
  isConditionallyApproving,
}: {
  userType: UserType;
  backgroundReport: BackgroundReport;
  applicationData: ApplicationData | undefined;
  emitMetric: EmitMetric;
  log: Log;
  requiresConditionalApproval: boolean;
  conditionallyApprove: ConditionallyApprove;
  isConditionallyApproving: boolean;
}) {
  const records = backgroundReport?.criminalReport?.records || [];
  const defaultRecords = records.filter((record) => !isSexOffenderRecord(record));
  const sexOffenderRecords = records.filter((record) => isSexOffenderRecord(record));

  const invalidReport = isReportUnavailable(backgroundReport?.criminalReport?.reportStatus);
  const alert = getFirstReportAlert(getBackgroundReportAlerts(userType, backgroundReport), ['criminalReport'])?.summary;

  useEffect(() => {
    countReportData(ReportType.BackgroundReport, 'criminal', records?.length, emitMetric);
  }, []);

  return (
    <>
      <Heading level={3} textStyle="heading-xs" css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        Criminal Record
      </Heading>
      {userType === UserType.Landlord && (requiresConditionalApproval || isConditionallyApproving) ? (
        <CriminalSectionObfuscatedContent
          conditionallyApprove={conditionallyApprove}
          isConditionallyApproving={isConditionallyApproving}
          emitMetric={emitMetric}
          log={log}
          applicationData={applicationData}
        />
      ) : (
        <>
          <Box css={{ marginBottom: 'loose' }}>
            <BackgroundReportAlert
              userType={userType}
              backgroundReport={backgroundReport}
              onlySources={['criminalReport']}
            />
          </Box>
          {!invalidReport && (
            <>
              <Paragraph>
                <Icon>
                  <IconSearchFilled />
                </Icon>{' '}
                {alert ??
                  (defaultRecords.length > 0 || sexOffenderRecords.length > 0 ? (
                    <>
                      Total
                      {defaultRecords.length > 0 && (
                        <strong>
                          {` ${defaultRecords.length} criminal ${
                            defaultRecords.length === 1 ? 'activity' : 'activities'
                          } `}
                        </strong>
                      )}
                      {defaultRecords.length > 0 && sexOffenderRecords.length > 0 && 'and '}
                      {sexOffenderRecords.length > 0 && (
                        <strong>
                          {sexOffenderRecords.length} sex offender {sexOffenderRecords.length === 1 ? 'hit' : 'hits'}
                        </strong>
                      )}{' '}
                      found in U.S. with available databases.
                    </>
                  ) : (
                    'No criminal activity or sex offender hits found in U.S. with available databases.'
                  ))}
              </Paragraph>

              {userType === UserType.Renter && requiresConditionalApproval && (
                <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter' }}>
                  For your protection, the criminal history section of your background report will be hidden from the
                  landlord, unless they choose to conditionally approve your application.{' '}
                  <Anchor href={CONDITIONAL_APPROVAL_URL} target="_blank" rel="noopener noreferrer">
                    Learn more
                  </Anchor>
                </Paragraph>
              )}

              <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter' }}>
                The result of criminal record searches may vary according to (1) collection restrictions by the
                different court jurisdictions which may result in some areas not having coverage and (2) reporting
                restrictions due to government regulations applicable to the rental property’s location. CIC applies
                industry best practices in the attempt to accurately match and report these records. The end user should
                compare the applicant supplied information with demographic data and any physical descriptors in
                determining a positive match prior to the end user making any decision.
              </Paragraph>
            </>
          )}
          {defaultRecords.length > 0 && (
            <>
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 'default',
                  marginBottom: 'default',
                }}
              >
                <Heading level={3} textStyle="heading-xs">
                  Nationwide criminal scan
                </Heading>
                <Heading level={4} textStyle="heading-xs">
                  {pluralize('Record', defaultRecords.length)}
                </Heading>
              </Box>
              <Accordion.Root>
                {defaultRecords.map((record, index) => (
                  <Accordion.Item key={index} value={`${index}`}>
                    <Accordion.Header>
                      <Heading level={4} css={{ textStyle: 'body' }}>
                        {getTitleCase(record.subject.sourceOfRecord)}
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <CriminalSectionContent record={record} />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </>
          )}
          {sexOffenderRecords.length > 0 && (
            <>
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 'default',
                  marginBottom: 'default',
                }}
              >
                <Heading level={3} textStyle="heading-xs">
                  Sex offender registry
                </Heading>
                <Heading level={4} textStyle="heading-xs">
                  {pluralize('Record', sexOffenderRecords.length)}
                </Heading>
              </Box>
              <Accordion.Root>
                {sexOffenderRecords.map((record, index) => (
                  <Accordion.Item key={index} value={`${index}`}>
                    <Accordion.Header>
                      <Heading level={4} css={{ textStyle: 'body' }}>
                        {getTitleCase(record.subject.sourceOfRecord)}
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <CriminalSectionContent record={record} />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </>
          )}
        </>
      )}
    </>
  );
}
